import { Stack } from '@mui/material';
import { formatMembershipName } from '@provision/service-level-utils';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { isNullish } from 'remeda';
import { FormChipGroup } from 'src/components/Forms/FormChipGroup/form-chip-group';
import { FormPriceField } from 'src/components/Forms/FormPriceField/form-price-field';
import { FormSelect } from 'src/components/Forms/FormSelect/form-select';
import { type Address, useOfferedProducts } from 'src/graphql';
import {
  convertPriceToDollars,
  getLengthInMonthsFromInterval,
  MAX_PRICE,
  PRE_SOLD_MEMBERSHIP_SECTION,
  preSoldLengths,
  PreSoldMembershipSectionField,
} from './util';

// literal keys: react-hook-form cannot resolve a nested path through enum-typed keys
export interface AddPreSoldMembershipFormValues {
  price: number;
  lengthInMonths: number;
  partnerProductId: string;
}

export interface PreSoldMembershipSectionValues {
  [PRE_SOLD_MEMBERSHIP_SECTION]: AddPreSoldMembershipFormValues;
}

interface AddPreSoldMembershipFormProps {
  address?: Address | void | null;
}

const lengthOptions = preSoldLengths.map((length) => ({
  id: length.label,
  value: length.value,
  label: length.label,
  testId: `pre-sold-membership-length-${length.value}`,
}));

function validateProduct(value: unknown) {
  return (typeof value === 'string' && value.trim().length > 0) || 'Select a service level';
}

function validatePrice(value: unknown) {
  if (isNullish(value) || Number.isNaN(value)) {
    return 'Enter the amount the client paid';
  }
  if (typeof value === 'number' && value <= 0) {
    return 'Enter a price greater than 0';
  }
  return true;
}

export const AddPreSoldMembershipForm = ({ address }: AddPreSoldMembershipFormProps) => {
  const { setValue } = useFormContext<PreSoldMembershipSectionValues>();

  const { offeredProducts, loading: isLoadingOfferedProducts } = useOfferedProducts({
    brandId: address?.brandId,
    partnerId: address?.partnerId,
  });

  const sortedOfferedProducts = useMemo(
    () => offeredProducts?.slice().sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [offeredProducts],
  );
  const productOptions = useMemo(
    () =>
      (sortedOfferedProducts ?? []).map((product) => ({
        id: product.partnerProductId,
        value: product.partnerProductId,
        label: formatMembershipName(product.displayName),
        testId: `pre-sold-membership-product-option-${product.partnerProductId}`,
      })),
    [sortedOfferedProducts],
  );

  const unscheduledPreSoldMembership = useMemo(() => {
    const psm = address?.preSoldMemberships?.[0];
    if (!psm || psm.isScheduled) {
      return null;
    }

    return psm;
  }, [address?.preSoldMemberships]);

  useEffect(() => {
    if (
      unscheduledPreSoldMembership?.id ||
      unscheduledPreSoldMembership?.preSoldMembershipId
    ) {
      if (unscheduledPreSoldMembership.partnerProductId) {
        setValue(
          PreSoldMembershipSectionField.PartnerProductId,
          unscheduledPreSoldMembership.partnerProductId,
          { shouldValidate: true },
        );
      }

      if (unscheduledPreSoldMembership.amount) {
        setValue(
          PreSoldMembershipSectionField.Price,
          convertPriceToDollars(unscheduledPreSoldMembership.amount),
          { shouldValidate: true },
        );
      }

      const lengthInMonths =
        unscheduledPreSoldMembership.intervalCount && unscheduledPreSoldMembership.intervalUnit
          ? getLengthInMonthsFromInterval(
              unscheduledPreSoldMembership.intervalCount,
              unscheduledPreSoldMembership.intervalUnit,
            )
          : preSoldLengths[0].value;

      setValue(PreSoldMembershipSectionField.LengthInMonths, lengthInMonths);
    }
  }, [unscheduledPreSoldMembership, setValue]);

  useEffect(() => {
    if (sortedOfferedProducts?.length && !unscheduledPreSoldMembership) {
      setValue(
        PreSoldMembershipSectionField.PartnerProductId,
        sortedOfferedProducts[0].partnerProductId,
        { shouldValidate: true },
      );
    }
  }, [sortedOfferedProducts, setValue, unscheduledPreSoldMembership]);

  return (
    <Stack gap={2}>
      <FormSelect
        type="select"
        name={PreSoldMembershipSectionField.PartnerProductId}
        label="Service Level"
        rules={{ validate: validateProduct }}
        showErrorText
        options={productOptions}
        disabled={isLoadingOfferedProducts}
        data-testid="pre-sold-membership-product"
      />
      <FormChipGroup
        name={PreSoldMembershipSectionField.LengthInMonths}
        label="Length"
        options={lengthOptions}
      />
      <FormPriceField
        name={PreSoldMembershipSectionField.Price}
        label="How much did the client pay?"
        rules={{ validate: validatePrice }}
        showErrorText
        emptyValue={0}
        max={MAX_PRICE}
        inputTestId="pre-sold-membership-price"
      />
    </Stack>
  );
};
